import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatRial } from '@/lib/formatMoney';
import { exportBsiTransactionsExcel } from '@/lib/banks/bsi/exportExcel';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbLastThirtyItem,
  type BsiStatementRegistrationSummary,
} from '@/lib/banks/bsi/api';
import BsiTransactionsTable from './BsiTransactionsTable';
import BsiRegisterStatementModal from './BsiRegisterStatementModal';
import { loadBsiStatementRegistrations, updateRegistrationAtIndex } from './statementRegistrations';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  preferredAccount?: string;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

const BsiLastThirtyTab: React.FC<Props> = ({
  sessionValid,
  preferredAccount,
  onMessage,
  onSessionExpired,
}) => {
  const [accounts, setAccounts] = React.useState<BsiIbAccountOption[]>([]);
  const [currencies, setCurrencies] = React.useState<BsiIbAccountOption[]>([]);
  const [accountNumber, setAccountNumber] = React.useState('');
  const [currency, setCurrency] = React.useState('');
  const [feeRial, setFeeRial] = React.useState(4500);
  const [items, setItems] = React.useState<BsiIbLastThirtyItem[]>([]);
  const [registrations, setRegistrations] = React.useState<BsiStatementRegistrationSummary[]>([]);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [loadingTx, setLoadingTx] = React.useState(false);
  const [registerIndex, setRegisterIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (preferredAccount) setAccountNumber(preferredAccount);
  }, [preferredAccount]);

  const loadAccounts = React.useCallback(async () => {
    setLoadingAccounts(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.lastThirtyAccounts();
      setAccounts(res.data.accounts || []);
      setCurrencies(res.data.currencies || []);
      setFeeRial(res.data.fee_rial || 4500);
      setAccountNumber((prev) => prev || preferredAccount || res.data.accounts?.[0]?.value || '');
      setCurrency((prev) => prev || res.data.currencies?.[0]?.value || '');
    } catch (e) {
      onMessage({ type: 'error', text: e instanceof Error ? e.message : 'خطا در دریافت حساب‌ها' });
    } finally {
      setLoadingAccounts(false);
    }
  }, [onMessage, preferredAccount]);

  React.useEffect(() => {
    if (sessionValid && accounts.length === 0) void loadAccounts();
  }, [sessionValid, accounts.length, loadAccounts]);

  const handleFetch = async () => {
    if (!accountNumber.trim()) {
      onMessage({ type: 'error', text: 'شماره حساب را انتخاب یا وارد کنید.' });
      return;
    }
    setLoadingTx(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.lastThirty({
        account_number: accountNumber.trim(),
        currency: currency || undefined,
      });
      const nextItems = res.data.items || [];
      setItems(nextItems);
      setFeeRial(res.data.fee_rial || 4500);
      const regs = await loadBsiStatementRegistrations(accountNumber.trim(), nextItems);
      setRegistrations(regs);
      onMessage({ type: 'success', text: res.message || 'سی گردش دریافت شد.' });
    } catch (err) {
      const text = err instanceof Error ? err.message : 'دریافت سی‌گردش ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoadingTx(false);
    }
  };

  const handleExport = async () => {
    if (!items.length) return;
    try {
      await exportBsiTransactionsExcel({ items, accountNumber });
      onMessage({ type: 'success', text: 'خروجی اکسل آماده شد.' });
    } catch (err) {
      onMessage({ type: 'error', text: err instanceof Error ? err.message : 'خروجی اکسل ناموفق بود.' });
    }
  };

  const registerItem = registerIndex != null ? items[registerIndex] ?? null : null;

  return (
    <>
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-base">سی گردش آخر</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sessionValid ? (
            <p className="text-sm text-amber-700">برای دریافت گردش، ابتدا از تب «ورود» وارد اینترنت‌بانک شوید.</p>
          ) : (
            <>
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                توجه: بانک برای هر بار «بارگذاری» سی‌گردش حدود {formatRial(feeRial)} ریال کارمزد می‌گیرد.
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>شماره حساب</Label>
                  {accounts.length > 0 ? (
                    <SearchableSelect
                      value={accountNumber}
                      onChange={setAccountNumber}
                      options={accounts.map((a) => ({ value: a.value, label: a.label || a.value }))}
                      placeholder={loadingAccounts ? 'در حال دریافت…' : 'انتخاب حساب'}
                      disabled={loadingAccounts}
                    />
                  ) : (
                    <Input
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="مثلاً 0118441572008"
                      inputMode="numeric"
                      disabled={loadingAccounts}
                    />
                  )}
                  {!loadingAccounts && accounts.length === 0 && (
                    <p className="text-xs text-amber-700">
                      لیست حساب از بانک نیامد — شماره حساب را دستی وارد کنید یا «بروزرسانی حساب‌ها» را بزنید.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>نوع ارز</Label>
                  <SearchableSelect
                    value={currency}
                    onChange={setCurrency}
                    options={
                      currencies.length
                        ? currencies.map((c) => ({ value: c.value, label: c.label || c.value }))
                        : [{ value: 'RIAL', label: 'ریال ایران' }]
                    }
                    placeholder="ارز"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button type="button" variant="outline" onClick={() => void loadAccounts()} disabled={loadingAccounts}>
                    {loadingAccounts ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    <span className="mr-1">بروزرسانی حساب‌ها</span>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleFetch()}
                    disabled={loadingTx || !accountNumber.trim()}
                  >
                    {loadingTx ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    بارگذاری
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {items.length > 0 && (
        <div className="lg:col-span-3">
          <BsiTransactionsTable
            title={`نتیجه — ${items.length} ردیف`}
            items={items}
            registrations={registrations}
            onRegister={setRegisterIndex}
            onExport={() => void handleExport()}
            creditLabel="جمع واریز (۳۰ ردیف)"
            debitLabel="جمع برداشت (۳۰ ردیف)"
          />
        </div>
      )}

      <BsiRegisterStatementModal
        open={registerIndex != null}
        accountNumber={accountNumber.trim()}
        reportSource="last_thirty"
        item={registerItem}
        existing={registerIndex != null ? registrations[registerIndex] : null}
        onClose={() => setRegisterIndex(null)}
        onSaved={(registration) => {
          if (registerIndex == null) return;
          setRegistrations((prev) => updateRegistrationAtIndex(prev, registerIndex, registration));
          onMessage({ type: 'success', text: 'ردیف گردش ثبت شد.' });
        }}
      />
    </>
  );
};

export default BsiLastThirtyTab;
